import React, { memo, Suspense } from 'react';
import { FiX } from 'react-icons/fi';
import widgetRegistry from '../widgets/widgetRegistry';
import { loadWidget } from '../utils/widgetLoader';
import { createDefaultWidgetData } from '../utils/widgetDefaults';

// Widget Preview Component
const WidgetPreview = memo(function WidgetPreview({ 
  widgetType, 
  category 
}: { 
  widgetType: string; 
  category: string; 
}) {
  console.log(`WidgetPreview: Rendering ${widgetType}`);
  
  try {
    const defaultData = createDefaultWidgetData(widgetType);
    console.log(`WidgetPreview: Default data for ${widgetType}:`, defaultData);
    
    // Use the new loadWidget function
    const LazyComponent = loadWidget(widgetType as any);
    
    return (
      <div className="w-full bg-gray-50 rounded border overflow-hidden relative">
        {/* Full component display without height restrictions */}
        <div className="w-full pointer-events-none select-none">
          <Suspense fallback={
            <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
              <span className="ml-2 text-sm text-gray-600">Loading...</span>
            </div>
          }>
            <div className="w-full bg-white">
              <LazyComponent
                data={defaultData as any}
                isEditing={false}
                onUpdate={() => {}}
                onNavClick={() => {}}
                onLogoClick={() => {}}
                styles={{ 
                  wrapper: { 
                    minHeight: "auto"
                  } 
                }}
              />
            </div>
          </Suspense>
        </div>
        
        {/* Widget label overlay */}
        <div className="absolute top-2 right-2 z-10">
          <span className="text-xs font-medium text-gray-800 bg-white/90 px-2 py-1 rounded shadow-sm">
            {widgetType}
          </span>
        </div>
      </div>
    );
  } catch (error) {
    console.error(`WidgetPreview: Error rendering ${widgetType}:`, error);
    return (
      <div className="w-full h-32 bg-red-50 rounded border border-red-200 flex items-center justify-center">
        <div className="text-center">
          <span className="text-red-500 text-sm block">Preview error</span>
          <span className="text-red-400 text-xs">{widgetType}</span>
        </div>
      </div>
    );
  }
});

// Dynamic widget categories function
const createWidgetCategories = (availableCategories?: string[]) => {
  const availableWidgets = Object.keys(widgetRegistry);

  const allCategories = {
    "Banner": availableWidgets.filter((w: string) => w.includes('Banner')),
    "Navigation": availableWidgets.filter((w: string) => w.includes('Nav')),
    "Experience": availableWidgets.filter((w: string) => w.includes('Experience')),
    "Popular Items": availableWidgets.filter((w: string) => w.includes('Popular')),
    "Combos": availableWidgets.filter((w: string) => w.includes('Combo')),
    "Testimonials": availableWidgets.filter((w: string) => w.includes('Testimonials')),
    "Footer": availableWidgets.filter((w: string) => w.includes('Footer')),
  };

  // If specific categories are requested, filter to only those
  if (availableCategories && availableCategories.length > 0) {
    const filteredCategories: { [key: string]: string[] } = {};
    availableCategories.forEach(category => {
      if (allCategories[category as keyof typeof allCategories]) {
        filteredCategories[category] = allCategories[category as keyof typeof allCategories];
      }
    });
    return filteredCategories;
  }

  return allCategories;
};

export interface WidgetSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectWidget: (widgetType: string) => void;
  title?: string;
  categories?: string[];
  maxWidth?: string;
  maxHeight?: string;
}

const WidgetSelectionModal = memo(function WidgetSelectionModal({
  isOpen,
  onClose,
  onSelectWidget,
  title = "Choose a Widget",
  categories,
  maxWidth = "max-w-6xl",
  maxHeight = "max-h-[90vh]",
}: WidgetSelectionModalProps) {
  if (!isOpen) return null;

  const widgetCategories = createWidgetCategories(categories);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg shadow-xl ${maxWidth} w-full mx-4 ${maxHeight} overflow-hidden`}>
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <FiX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {Object.entries(widgetCategories).map(([category, widgets]) => (
            <div key={category} className="mb-8">
              <h3 className="text-lg font-medium text-gray-800 mb-4">{category}</h3>
              <div className="grid grid-cols-1 gap-6">
                {widgets.map((widgetType: string) => (
                  <div
                    key={widgetType}
                    onClick={() => onSelectWidget(widgetType)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectWidget(widgetType);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Select ${widgetType} widget`}
                    className="p-3 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent group cursor-pointer"
                  >
                    {/* Widget Preview */}
                    <div className="mb-3">
                      <WidgetPreview widgetType={widgetType} category={category} />
                    </div>
                    
                    {/* Widget Info */}
                    <div className="space-y-1">
                      <div className="font-medium text-gray-900 text-sm group-hover:text-blue-700 transition-colors">
                        {widgetType}
                      </div>
                      <div className="text-xs text-gray-500">
                        {category} widget for your restaurant
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

WidgetSelectionModal.displayName = 'WidgetSelectionModal';

export default WidgetSelectionModal;
